/* eslint-disable @typescript-eslint/no-empty-function */
import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { BigButton } from '@/components/big-button';
import {
  Experience,
  ExperienceVariant,
  useExperienceApi,
} from '@/webapi/use-experience-api';
import { VSpace } from '@/components/spacing';
import { DeviceType } from '@/utils/definitions';
import { mutateFn, useComplexState } from '@/utils/use-complex-state';
import { CONTROL } from '@/features/details/shared';
import { breakpoints } from '@/components/responsive';
import { InlineVariantsLayoutControlGroup } from '@/features/editor/widgets/shared/inline-variants-layout/control';
import { InlineVariantsLayoutVariation } from '@/features/editor/widgets/shared/inline-variants-layout/variant';

const Container = styled.div`
  width: 340px;
  border-radius: 15px;
  background-color: white;
  padding: 2rem;
  border: solid 0.6px rgba(151, 151, 151, 0.1);
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.09), 0 1px 4px 0 rgba(7, 6, 6, 0.04);

  ${breakpoints.down(`md`)} {
    width: 100%;
    border-radius: 2rem 2rem 0 0;
  }
`;

const SubTitle = styled.p`
  text-align: start;
  color: black;
  width: 100%;
  font-size: 18px;
  user-select: none;
  font-family: Inter, serif;
  font-weight: 600;
  letter-spacing: 0.11px;
  margin: 0 0 2.5rem 0;
  padding-bottom: 2rem;
  border-bottom: 2px solid #e7e8e9;
`;

const ButtonContainer = styled.div`
  width: 100%;
  margin: 3rem 0 0 0;
`;

type Props = {
  experience: Experience;
  setExperience: (e: Experience) => void;
  togglePopover: () => void;
};

function SetVariants({
  device,
  loading,
  onClick,
  showAreYouSure,
  variants,
  setVariants,
  initialChance,
}: {
  showAreYouSure: boolean;
  initialChance: string;
  device: DeviceType | undefined;
  onClick: () => void;
  loading: boolean;
  variants: ExperienceVariant[];
  setVariants: (fn: mutateFn<ExperienceVariant[]>) => void;
}) {
  const isDisabled =
    initialChance === getIniitalChance(variants) ||
    loading ||
    variants.reduce((p, c) => getChance(c) + p, 0) > 100;
  const changeAllocation = (v: ExperienceVariant) => {
    setVariants((draft: ExperienceVariant[]) => {
      draft.forEach(({ id }, index) => {
        if (id === v.id) {
          draft[index] = v;
        }
      });
      return draft;
    });
  };

  return (
    <div style={{ display: showAreYouSure ? `none` : `block` }}>
      <SubTitle>Set Variants</SubTitle>
      <InlineVariantsLayoutControlGroup
        device={device}
        chances={variants?.map((v) => getChance(v))}
      />
      <div>
        {variants.map((v) => (
          <>
            <VSpace value={2} />
            <InlineVariantsLayoutVariation
              key={v.id}
              disableNameEdit
              _variants={variants}
              _changeAllocation={changeAllocation}
              device={device}
              variant={v}
              publishedChance={getChance(v)}
            />
          </>
        ))}
      </div>
      <ButtonContainer>
        <BigButton
          onClick={onClick}
          disabled={isDisabled}
          color="white"
          background="#3451fa"
          noTransform
          fillWidth
          type="submit"
          size="medium"
        >
          {loading ? `...` : `Apply`}
        </BigButton>
      </ButtonContainer>
    </div>
  );
}

function getIniitalChance(variants: ExperienceVariant[]) {
  return clone(variants)
    .sort((a, b) => a.id.localeCompare(b.id))
    .map((v) => getChance(v))
    .join(`|`);
}

export const PopoverContent: FC<Props> = ({
  experience,
  togglePopover,
  setExperience,
}) => {
  const { loading, updateExperienceAllocation } = useExperienceApi(
    experience.lastDevicePreview,
    experience,
  );
  const [variants, setVariants] = useComplexState(
    experience?.variants?.filter((v) => v.name !== CONTROL),
  );

  const [showAreYouSure, setShowAreYouSure] = useState(false);

  const [initialChance, setInitialChance] = useState(null);
  useEffect(() => {
    setInitialChance(getIniitalChance(variants));
  }, []);
  const onYes = async () => {
    const clonedExperience = clone(experience);
    const updatedVariants = variants.map((v) => ({
      id: v.id,
      chance: v.chance || 0,
      publishedChance: v.chance || 0,
    }));

    updatedVariants.forEach((v) => {
      clonedExperience.variants.forEach((variant, index) => {
        if (variant.id === v.id) {
          clonedExperience.variants[index].chance = v.chance;
          clonedExperience.variants[index].publishedChance = v.chance;
        }
      });
    });
    const controlIdx = clonedExperience.variants.findIndex(
      (v) => v.name === CONTROL,
    );
    const controlChance =
      100 - (updatedVariants.reduce((p, c) => p + c.chance, 0) || 0);
    clonedExperience.variants[controlIdx].chance = controlChance;
    clonedExperience.variants[controlIdx].publishedChance = controlChance;

    clonedExperience.variants = clonedExperience.variants.map((v) => ({
      id: v.id,
      chance: v.chance,
      publishedChance: v.publishedChance,
    }));

    const updatedExp = await updateExperienceAllocation(
      clonedExperience,
      clonedExperience.variants,
    );
    updatedExp.metricsStartAt = new Date().toISOString();

    setExperience(updatedExp);
    togglePopover();
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      togglePopover();
    }, 10000);

    return () => clearTimeout(timeout);
  }, [variants, showAreYouSure]);

  return (
    <Container>
      <AreYouSure
        showAreYouSure={showAreYouSure}
        loading={loading}
        onYes={onYes}
        onNo={() => setShowAreYouSure(false)}
      />
      <SetVariants
        initialChance={initialChance}
        variants={variants}
        setVariants={setVariants}
        showAreYouSure={showAreYouSure}
        device={experience?.lastDevicePreview}
        onClick={() => {
          setShowAreYouSure(true);
        }}
        loading={loading}
      />
    </Container>
  );
};

const AreYouSureContainer = styled.div`
  font-family: Inter, serif;
  text-align: center;
  p:first-child {
    user-select: none;
    font-size: 21px;
    color: black;
    font-weight: 600;
  }
  p:nth-child(2) {
    user-select: none;
    color: #777c8b;
    font-size: 14px;
  }
  div {
    width: 100%;
    grid-template-columns: 1fr 1fr;
    grid-gap: 2rem;
    display: grid;
    flex-direction: row;
  }
`;

function getChance(variant: ExperienceVariant) {
  return variant.publishedChance || variant.chance || 0;
}

function AreYouSure({
  loading,
  onYes,
  onNo,
  showAreYouSure,
}: {
  showAreYouSure: boolean;
  loading: boolean;
  onYes: () => Promise<void>;
  onNo: () => void;
}) {
  return (
    <AreYouSureContainer
      style={{
        display: showAreYouSure ? `block` : `none`,
        opacity: loading ? `0.5` : `1`,
      }}
    >
      <p>Are you sure you want to change the allocation?</p>
      <p>A new test version will be created</p>
      <div>
        <BigButton
          onClick={onYes}
          disabled={loading}
          border="2px solid #C8CACB"
          background="white"
          color="#97A0A8"
          noShadow
          noTransform
          size="large"
          fillWidth
        >
          Yes
        </BigButton>
        <BigButton
          onClick={onNo}
          disabled={loading}
          border="2px solid #C8CACB"
          background="white"
          color="#97A0A8"
          noShadow
          noTransform
          size="large"
          fillWidth
        >
          No
        </BigButton>
      </div>
    </AreYouSureContainer>
  );
}

function clone(anything: any) {
  return JSON.parse(JSON.stringify(anything));
}
